import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default markers in React Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom driver van icon
const driverIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="2" y="8" width="16" height="8" rx="2" fill="#3B82F6"/>
      <rect x="18" y="10" width="4" height="6" rx="1" fill="#3B82F6"/>
      <circle cx="6" cy="18" r="2" fill="#1F2937"/>
      <circle cx="16" cy="18" r="2" fill="#1F2937"/>
      <rect x="4" y="6" width="12" height="2" fill="#3B82F6"/>
      <rect x="19" y="12" width="2" height="2" fill="#FFFFFF"/>
    </svg>
  `),
  iconSize: [24, 24],
  iconAnchor: [12, 24],
  popupAnchor: [0, -24]
});

// Custom facility icon
const facilityIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="3" y="8" width="18" height="13" fill="#7C3AED"/>
      <rect x="5" y="10" width="2" height="3" fill="#FFFFFF"/>
      <rect x="9" y="10" width="2" height="3" fill="#FFFFFF"/>
      <rect x="13" y="10" width="2" height="3" fill="#FFFFFF"/>
      <rect x="17" y="10" width="2" height="3" fill="#FFFFFF"/>
      <polygon points="3,8 12,3 21,8" fill="#7C3AED"/>
    </svg>
  `),
  iconSize: [20, 20],
  iconAnchor: [10, 20],
  popupAnchor: [0, -20]
});

const DriverMap = ({ 
  driverLocations = [], 
  facilities = [], 
  routeHistory = [],
  selectedDriver = null,
  onDriverSelect = null,
  height = "400px"
}) => {
  const mapRef = useRef();

  // Default to Los Angeles area (where community facilities are located)
  const defaultCenter = [34.0522, -118.2437];
  const defaultZoom = 11;

  useEffect(() => {
    // Auto-refresh driver locations every 30 seconds
    const interval = setInterval(() => {
      // This will be handled by parent component
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="driver-map-container" style={{ height, width: '100%' }}>
      <MapContainer
        center={defaultCenter}
        zoom={defaultZoom}
        style={{ height: '100%', width: '100%' }}
        ref={mapRef}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        
        {/* Driver Locations */}
        {(driverLocations || []).map((driver) => (
          <Marker
            key={driver.id}
            position={[driver.location.latitude, driver.location.longitude]}
            icon={driverIcon}
            eventHandlers={{
              click: () => onDriverSelect && onDriverSelect(driver)
            }}
          >
            <Popup>
              <div className="driver-popup">
                <h4 className="font-semibold">{driver.driver_name}</h4>
                <p className="text-sm">Status: {driver.status}</p>
                <p className="text-xs">
                  Last Updated: {new Date(driver.last_updated).toLocaleTimeString()}
                </p>
                {driver.current_task_id && (
                  <p className="text-xs">
                    Current Task: {driver.current_task_id.slice(-6)}
                  </p>
                )}
                {driver.speed && (
                  <p className="text-xs">
                    Speed: {driver.speed.toFixed(1)} km/h
                  </p>
                )}
                <p className="text-xs">
                  Accuracy: ±{driver.location.accuracy?.toFixed(0)}m
                </p>
              </div>
            </Popup>
            
            {/* GPS accuracy circle */}
            <Circle
              center={[driver.location.latitude, driver.location.longitude]}
              radius={driver.location.accuracy || 50}
              fillColor="blue"
              fillOpacity={0.1}
              color="blue"
              weight={1}
            />
          </Marker>
        ))}
        
        {/* Facility Locations */}
        {(facilities || []).map((facility) => (
          <Marker
            key={facility.id}
            position={[facility.latitude || 34.0522, facility.longitude || -118.2437]}
            icon={facilityIcon}
          >
            <Popup>
              <div className="facility-popup">
                <h4 className="font-semibold">{facility.name}</h4>
                <p className="text-sm">{facility.address}</p>
                <p className="text-xs">PIN: {facility.pin}</p>
                <div className="facility-stats mt-2">
                  <p className="text-xs">
                    Pending: {facility.pending_orders || 0}
                  </p>
                  <p className="text-xs">
                    Completed: {facility.completed_pickups || 0}
                  </p>
                </div>
              </div>
            </Popup>
            
            {/* Geofence circle around facility */}
            <Circle
              center={[facility.latitude || 34.0522, facility.longitude || -118.2437]}
              radius={100} // 100m geofence
              fillColor="purple"
              fillOpacity={0.1}
              color="purple"
              weight={2}
              dashArray="5, 5"
            />
          </Marker>
        ))}
        
        {/* Route History for Selected Driver */}
        {selectedDriver && (routeHistory || []).length > 0 && (
          (routeHistory || [])
            .filter(route => route.driver_id === selectedDriver.driver_id)
            .map(route => 
              (route.route_points || []).map((point, index) => (
                <Circle
                  key={`${route.id}-${index}`}
                  center={[point.latitude, point.longitude]}
                  radius={5}
                  fillColor="green"
                  fillOpacity={0.8}
                  color="green"
                  weight={1}
                />
              ))
            )
        )}
      </MapContainer>
    </div>
  );
};

export default DriverMap;